import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAccountSettings } from './useAccountSettings'

/**
 * Adopt the account's persisted language (`settings.language`) on a FRESH device
 * — so a Thai account lands in Thai after login even when the device locale is
 * English and nothing is stored yet (B-026a — Android showed English-only with
 * no detection/sync).
 *
 * Precedence: an explicit per-device choice ALWAYS wins. If `localStorage`
 * already holds a `language` (a Header toggle, a value we previously persisted,
 * device detection that ran, or a test's seeded storageState), we do NOT
 * override it with the account default. Otherwise the account preference would
 * flip the UI on every load and stomp the user's/device's own choice — and would
 * silently switch language mid-session once an operator saves account settings
 * (which is exactly what broke the English-heading e2e assertions: the settings
 * save persisted `language: 'th'`, then this hook flipped later pages to Thai).
 *
 * On a fresh device we apply the account language once and persist it, so the
 * next load takes the fast localStorage path. The effect keys on the account
 * language VALUE, so it runs when the account loads/changes — never re-firing on
 * an unrelated render.
 *
 * Uses the live i18n instance from useTranslation() rather than importing the
 * `@/i18n` init module, so consumers (e.g. ProtectedLayout via the layout
 * barrel) don't pull i18n's module-eval side effects.
 */
export function useLanguageSync(): void {
  const { i18n } = useTranslation()
  const { data: accountSettings } = useAccountSettings()
  const accountLang = accountSettings?.settings?.language

  useEffect(() => {
    if (accountLang !== 'en' && accountLang !== 'th') return
    // Respect an explicit per-device choice — only adopt the account language
    // when this device has none stored yet (fresh install).
    let stored: string | null = null
    try {
      stored = localStorage.getItem('language')
    } catch {
      // localStorage unavailable — treat as no stored choice.
    }
    if (stored) return
    if (i18n.language === accountLang) return
    i18n.changeLanguage(accountLang)
    try {
      localStorage.setItem('language', accountLang)
    } catch {
      // localStorage unavailable (private mode / SSR) — language still applied in-memory.
    }
  }, [accountLang, i18n])
}
